<script setup>
import Multiselect from 'vue-multiselect';
import MetricCard from './overview/MetricCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  dateRange: {
    type: Object,
    required: true,
  },
  operatorOptions: {
    type: Array,
    default: () => [],
  },
  selectedOperators: {
    type: Array,
    default: () => [],
  },
  conversationId: {
    type: String,
    default: '',
  },
  t: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits([
  'updateDateRange',
  'updateSelectedOperators',
  'updateConversationId',
  'apply',
]);

const handleDateRangeChange = (field, value) => {
  emit('updateDateRange', { field, value });
};

const handleConversationInput = event => {
  emit('updateConversationId', event.target.value);
};
</script>

<template>
  <MetricCard
    :header="t('RESOLUTION_STATISTICS.FILTERS.HEADER')"
    :is-loading="false"
  >
    <div class="flex flex-wrap gap-4 items-end">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('RESOLUTION_STATISTICS.FILTERS.START_DATE') }}
        </label>
        <input
          type="date"
          :value="dateRange.start"
          class="px-3 py-2 border border-n-slate-7 rounded-lg bg-n-background text-n-slate-12"
          @input="e => handleDateRangeChange('start', e.target.value)"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('RESOLUTION_STATISTICS.FILTERS.END_DATE') }}
        </label>
        <input
          type="date"
          :value="dateRange.end"
          class="px-3 py-2 border border-n-slate-7 rounded-lg bg-n-background text-n-slate-12"
          @input="e => handleDateRangeChange('end', e.target.value)"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('RESOLUTION_STATISTICS.FILTERS.OPERATORS') }}
        </label>
        <div class="min-w-[250px]">
          <Multiselect
            :model-value="selectedOperators"
            :options="operatorOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :placeholder="
              t('RESOLUTION_STATISTICS.FILTERS.OPERATORS_PLACEHOLDER')
            "
            label="label"
            track-by="value"
            :preselect-first="false"
            @update:model-value="
              value => emit('updateSelectedOperators', value)
            "
          >
            <template #selection="{ values, isOpen }">
              <span v-if="values.length && !isOpen" class="multiselect__single">
                {{
                  t('RESOLUTION_STATISTICS.FILTERS.OPERATORS_SELECTED', {
                    count: values.length,
                  })
                }}
              </span>
            </template>
          </Multiselect>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('RESOLUTION_STATISTICS.FILTERS.CONVERSATION') }}
        </label>
        <input
          :value="conversationId"
          type="number"
          min="1"
          class="px-3 py-2 border border-n-slate-7 rounded-lg bg-n-background text-n-slate-12"
          :placeholder="
            t('RESOLUTION_STATISTICS.FILTERS.CONVERSATION_PLACEHOLDER')
          "
          @input="handleConversationInput"
        />
      </div>

      <Button variant="primary" size="md" @click="emit('apply')">
        {{ t('RESOLUTION_STATISTICS.FILTERS.APPLY') }}
      </Button>
    </div>
  </MetricCard>
</template>
